// The site layer of the WebGL2 client (R431): the port of
// `render/sites.ts` onto instance batches.
//
// The public surface is the PixiJS layer's, name for name — `add`,
// `remove`, `setOwner`, `setSquadIds`, `get`, `pick`, `pickDeposit`,
// `structures`, `hoverRing` — so the handler code of
// `main.ts` and of `arcade/match.ts` copies over unchanged.
//
// The PixiJS layer holds a `Container`, a `Graphics` and sometimes a
// beacon and a halo per site, and it shares the mark geometry through a
// `GraphicsContext` cache (R193). This holds one dense record array and one
// `Map<id, Site>`, and it packs the marks that are ON SCREEN into two
// batches: the atlas sprites, and the control points' rotating haloes.
//
// The mark art is the same art. The atlas bakes it from `render/marks.ts`
// and `render/hold-marks.ts`, under the key scheme of `gfx/atlas.ts`.
//
// **A hold wears a chart symbol** (R633, mockup round thirty-two,
// `docs/design/ui-map-interface.md` §6.2). Which of the six it wears is the
// `hold` field of the Marks body of the player's look (`look.marks.hold`),
// and which of the two readings it is drawn in follows the `chart` setting,
// so both reach the layer through `setStyle`, and a change there re-keys
// every structure on the map. A faction installation wears card 02, the
// light, in the same reading.
//
// **A hold wears the mark of its rank** (R185, R834, §6.2). The `ownHold`
// field of a Marks body picks what goes under the commander's own symbol
// and `squadHold` picks what goes under a squadmate's, so both join the
// mark key through `setLook`. A still mark is baked into the hold's cell.
// A mark that MOVES is one cell a part, drawn beside the beacon light and
// under the same rule: only an own hold animates.
//
// **A control point wears the structure symbol of its object kind** (R1103,
// R1104, §6.2). The `structure` field of a Marks body names the set of all
// nine kinds, and it reaches the layer through `setLook`. The KIND, the
// contested flag and the attacker come from the Sieges frame through
// `setObjects`, because a `Site` record carries the site kind alone; the
// layer keeps all three, and `restyle` never loses them. A layer built
// before the first Sieges frame keys its control points at the unspecified
// kind and re-keys them when the frame lands.
//
// **The cartouche carries text, and text is not baked.** A name would need
// one atlas cell for every hold on the map, so the label is a glyph run of
// the chart's own `ChartGlyphs` — the same two vendored faces the furniture
// writes with (R548). The run is laid out again every frame from the atlas
// entries of that frame, so a cell that was baked before the faces landed
// heals itself at the next frame and there is nothing to invalidate.

import { outside, type DrawCtx, type FrameInfo, type Layer, type ViewRect } from '../layer';
import {
  siteCpKey, siteDepositKey, siteHoldKey, siteHoldLightKey, siteHoldRuinKey,
  siteInstKey, siteInstRuinKey,
} from '../gfx/atlas';
import { depositLevel } from '../../render/marks';
import type { GatherField } from '../../render/gather-state';
import {
  AUTO_SCENE_LOOK, type FactionSiteStyle, type SceneLook,
} from '../../render/scene-look';
import type {
  DepositStyle, OwnHoldStyle, SquadHoldStyle, StructureSymbol,
} from '../../ui/settings';
// The live halo of the `default` structure symbol set (R1104).
import { DEFAULT_HALOS, structureKindOf } from '../../render/structures';
import {
  holdLabel, HOLD_LABEL_FRAME, HOLD_LABEL_STRIKE,
  DEFAULT_HOLD_SYMBOL, type HoldEra, type HoldLabelSlot, type HoldRank, type HoldSymbolId,
} from '../../render/hold-marks';
import {
  OWN_HOLD_MARKS, type HoldAnimPart,
} from '../../render/hold-rank-marks';
import { ChartGlyphs } from '../chart/glyphs';
import type { BatchSource, CircleSink, QuadSink, SegmentSink, SpriteEntry } from './batches';
import { growColumns, moveSlot } from './shared';
import {
  ARCADE_CAPTURE, MARK_INKS, WARD_RING_ALPHA, WARD_RING_DASHES, WARD_RING_DASH_FILL, WARD_RING_WIDTH,
} from '../../gen/presentation';
import { OBJECTIVE_FLAG_KEY } from '../../render/objective-flag';
import { cellCenter, factionColor, HEX_SIZE, MPX_PER_PX } from '../../world';
import type { Site, SonarRing } from '../../gen/proto/aquaphobia';
import { SiteKind } from '../../gen/proto/aquaphobia';
import type { HoverRing } from '../../render/sonar-types';
import { TAU } from '../../render/math';

// ---------------------------------------------------------------------------
// The figures the PixiJS layer states as literals
// ---------------------------------------------------------------------------

/** The control point's rotating dashed halo (`marks.ts:222-229`). */
const CP_HALO_R = 22;
const CP_HALO_WIDTH = 1.5;
const CP_HALO_DASHES = 8;
const CP_HALO_FILL = 0.6;
const CP_HALO_ALPHA = 0.5;
/** Radians a millisecond, from `s.ring.rotation += deltaMS / 4000`. */
const CP_HALO_SPIN_PER_MS = 1 / 4000;
/**
 * The halo alpha of card 04, once the Sieges frame has named the kind
 * (R1104): a held object is brighter than a neutral one.
 */
const CP_HALO_HELD_ALPHA = 0.7;
const CP_HALO_NEUTRAL_ALPHA = 0.45;
/**
 * The set that keeps the live halo: the AUTO value of the `structure` field
 * (R1103). It is a literal here, because this layer reads no balance pack,
 * and a test pins it against the generated list.
 */
const DEFAULT_STRUCTURE_SET: StructureSymbol = 'default';

/** The beacon blink (`render/sites.ts`). */
const BLINK_BASE = 0.4;
const BLINK_SWING = 0.6;
const BLINK_HZ = 2;
// The deposit shimmer and the worked-out fade are gone (R475). A field says
// its stock with the fill of its diamond and its state with the fill against
// the bare rim, so every field sits at alpha 1 and no site pays for a sine.
/** The restock bloom: it decays over this many ms and swells by this much. */
const RESTOCK_MS = 1600;
const RESTOCK_SCALE = 0.5;

/** The ruin's blue-grey, which a fallen hold writes its text in (R126). */
const RUIN_INK = MARK_INKS.ruinInk;
/** The white the viewer's own hold writes in, as its mark does: a dark
 *  core in the light identity (R3296). */
const WHITE = MARK_INKS.white;
/** The tint of a cell baked in its own inks: the quad keeps them. */
const NO_TINT = 0xffffff; // mask
/** How many letters of a commander's name the historical cartouche holds. */
const INITIALS_MAX = 3;

/**
 * Every typed column of one number a slot, by field name: the list the grow
 * and the swap-remove share (R2212). The six key and label lists are plain
 * arrays, so `remove` moves them with the columns and trims them itself.
 */
export const SITE_COLUMNS = [
  'id', 'x', 'y', 'restocked', 'kind', 'hidden', 'mate', 'effect', 'objKind',
  'contested', 'attacker',
] as const;

/** The six plain lists of one entry a slot, which a grow leaves alone. */
const SITE_LISTS = [
  'markKey', 'beaconKey', 'animParts', 'labelSlot', 'labelText', 'labelInk',
] as const;

/** How many sites the arrays hold before the first doubling. */
const START_CAPACITY = 512;

/** How a cartouche label is laid out. Built once, so a frame allocates none. */
const LABEL_RUN = { anchor: 'centre' } as const;

/**
 * The text of one hold's cartouche (R633).
 *
 * The historical reading is the commander's initials in the Fell face —
 * the first letter of up to three words of the name, joined by a stop, as
 * "C.K" — and a fallen hold is a cross alone. The modern reading is the
 * empire's designation in the mono face: the commander's first letter, a
 * dash and the owner's player id, as "K-7".
 *
 * The letters are raised to capitals, because the two cards write in
 * capitals and a chart label is read at ten pixels.
 */
export function holdText(
  era: HoldEra, ruin: boolean, commander: string | undefined, ownerPlayer: number,
): string {
  // A record with no commander at all is a record a server older than R633
  // wrote. It writes no text, exactly as a blank name does.
  const name = (commander ?? '').trim();
  if (era === 'h') {
    if (ruin) return '†';
    const initials: string[] = [];
    for (const word of name.split(/\s+/)) {
      if (word === '') continue;
      initials.push([...word][0].toUpperCase());
      if (initials.length === INITIALS_MAX) break;
    }
    return initials.join('.');
  }
  if (name === '') return '';
  return `${[...name][0].toUpperCase()}-${ownerPlayer}`;
}

/** The four fields of a look this layer's marks answer to (R814, R1103). */
export interface SiteLook {
  deposit: DepositStyle;
  ownHold: OwnHoldStyle;
  squadHold: SquadHoldStyle;
  /** The set all nine strategic objects are drawn as (R1103). */
  structure: StructureSymbol;
  /** The charge beside a hold and an installation of another faction
   *  (R1568, card 01 of round 075): `charge`, or `off`. */
  factionSite: FactionSiteStyle;
}

/**
 * What one strategic object is, as the Sieges frame states it (R1104).
 *
 * A `Site` record carries the SITE kind and never the OBJECT kind, and a
 * `SiteOwner` frame carries neither. So the layer learns the three from the
 * Sieges frame and KEEPS them: nothing else on the wire can say them again.
 */
export interface StructureState {
  siteId: number;
  /** The `ObjKind` number. 0 is the kind the layer has not learned yet. */
  kind: number;
  /** An attack pledge stands against the object. */
  contested: boolean;
  /** The faction that pledged, or 0 while none has. */
  attacker: number;
}

/**
 * One player base, as the effects layer reads it (R910).
 *
 * A hold's effect draws UNDER every wake, hull and round (the owner's
 * ruling), so it has a slot of its own and this snapshot of its own.
 */
export interface HoldSnap {
  id: number;
  /** The hold's centre, in world px. */
  x: number; y: number;
  faction: number;
  /** The account that owns the hold, which fixes the effect's phase. */
  ownerPlayer: number;
  /** The owner's hold effect, a catalog wire id; 0 = none. */
  effect: number;
  /** The hold is off the glass. */
  hidden: boolean;
}

/**
 * One held Coral Ward, as the two map layers read it (R1082).
 *
 * The site layer draws the ring at the Ward's reach, and the fleet layer
 * raises the shield plus on a friendly hull inside it. Both take the
 * centre in world px and the faction that holds the object.
 */
export interface WardAura {
  x: number;
  y: number;
  faction: number;
  /**
   * A public Ward of an arcade map: neutral, not capturable, and it mends
   * every hull in its aura (arcade.md §19.5, R2029). Its ring wears the
   * neutral grey, and the fleet layer raises the plus on every own hull.
   * Absent is false, which every season Ward is.
   */
  public?: boolean;
}

/**
 * One running capture of an arcade structure (arcade.md §19.2, R2029):
 * the outline of its capture ring and the count in the taker's ink.
 *
 * The scene builds one record per capturable structure at the hello and
 * keeps it; the match writes `taker` and `leftWallSec` in place on every
 * frame, so the count runs down between two phase frames and a frame
 * allocates nothing (R131). A record with `taker` 0 draws nothing.
 */
export interface CaptureMark {
  siteId: number;
  /** The centre of the structure, in world px. */
  x: number;
  y: number;
  /** The outer edges of the cell and the six cells round it, in world px:
   *  x0, y0, x1, y1 for each edge. */
  edges: Float64Array;
  taker: number;
  leftWallSec: number;
}

/** The outline and the count of a running capture (R2029), from
 *  `[presentation.arcade.capture]`. */
const CAPTURE_RING_WIDTH = ARCADE_CAPTURE.ringPx;
const CAPTURE_RING_ALPHA = ARCADE_CAPTURE.ringAlpha;
const CAPTURE_COUNT_PX = ARCADE_CAPTURE.countPx;
const CAPTURE_COUNT_DY = ARCADE_CAPTURE.countDyHex;

export class SiteLayer implements Layer {
  readonly name = 'sites';
  private shown = true;

  private readonly quads: QuadSink;
  private readonly halos: CircleSink;
  /**
   * The cartouche labels (R633), in a batch of their own.
   *
   * They are atlas sprites like the marks, so one batch would draw them —
   * but a batch draws in the order it was packed, and a mark packed after a
   * neighbour's label would sit on top of that label. One more flush a
   * frame buys the guarantee that every label reads.
   */
  private readonly text: QuadSink;
  /**
   * The frame of a modern cartouche (R633): the box round an own or a
   * squadmate's designation, the rule under another's, the bar across a
   * ruin's. It is sized to the run, so it is the layer's and not a cell's.
   */
  private readonly frames: SegmentSink;
  private readonly sprite: (key: string) => SpriteEntry;
  /** The flat white texel, tinted for the fill of the cartouche's box. */
  private readonly white: SpriteEntry;
  /** The two vendored chart faces, as sprite strings (R548). */
  private readonly glyphs: ChartGlyphs;

  // -- the dense record set -------------------------------------------------
  private cap = START_CAPACITY;
  private n = 0;
  private idToIndex = new Map<number, number>();
  /** The wire's own record, which `get()` hands back untouched. */
  private data = new Map<number, Site>();

  private id = new Uint32Array(START_CAPACITY);
  // The two positions are Float64: a site never moves, so the precision is
  // free, and `hoverRing` compares against a figure the wire sent in
  // millipixels.
  private x = new Float64Array(START_CAPACITY);
  private y = new Float64Array(START_CAPACITY);
  private restocked = new Float32Array(START_CAPACITY);
  private kind = new Uint8Array(START_CAPACITY);
  private hidden = new Uint8Array(START_CAPACITY);
  private mate = new Uint8Array(START_CAPACITY);
  /**
   * The owner's hold effect, a catalog wire id of
   * `data/contracts/effects.toml`; 0 = none (R910). A hold is public, so
   * the server states the number with no gate, and every `SiteOwner` of a
   * player base restates it.
   */
  private effect = new Uint32Array(START_CAPACITY);
  /**
   * What object stands on a control point, from the Sieges frame (R1104).
   *
   * `objKind` is the `ObjKind` number, `contested` says that an attack
   * pledge stands, and `attacker` is the faction that pledged. A site the
   * layer built before the first Sieges frame carries kind 0, the
   * unspecified kind, and it is re-keyed when the frame lands.
   */
  private objKind = new Uint8Array(START_CAPACITY);
  private contested = new Uint8Array(START_CAPACITY);
  private attacker = new Uint8Array(START_CAPACITY);
  /** Whether a replay film runs (R2404). A pledge is the siege of today,
   *  so a film draws no control point contested. */
  private filming = false;
  /**
   * The mark key of every site, rebuilt only on add, on an ownership frame
   * and on a squad roster — never per frame (the plan's C.2).
   */
  private markKey: string[] = [];
  /** The beacon key, or an empty string for a site that carries no light. */
  private beaconKey: string[] = [];
  /**
   * The cells of the hold mark that move, or null where none moves (R834).
   *
   * It is the table's own frozen array, so a frame allocates nothing: the
   * layer reads the parts of the value in force and writes one quad each.
   * Only an own hold ever carries one, because only an own hold animates.
   */
  private animParts: (readonly HoldAnimPart[] | null)[] = [];
  /**
   * The cartouche's text slot, or null where the symbol writes nothing.
   *
   * The slot, the string and the ink are all cold data: they answer the
   * symbol, the reading, the rank and the commander's name, and every one
   * of those four changes through `restyle`. A frame reads them and builds
   * no string of its own (R131).
   */
  private labelSlot: (HoldLabelSlot | null)[] = [];
  private labelText: string[] = [];
  private labelInk: number[] = [];

  private time = 0;
  private structuresCache: { x: number; y: number; faction: number; kind: number }[] | null = null;
  private squadIds = new Set<number>();

  /**
   * The three look ids a site mark answers to (themes.md §3.5, R814).
   *
   * The deposit id (R832) and the two hold marks (R834) are part of a
   * field's mark key, so a change to one re-keys every deposit or every
   * hold on the map.
   */
  private readonly looks: SiteLook = {
    deposit: AUTO_SCENE_LOOK.deposit,
    ownHold: AUTO_SCENE_LOOK.ownHold,
    squadHold: AUTO_SCENE_LOOK.squadHold,
    structure: AUTO_SCENE_LOOK.structure,
    factionSite: AUTO_SCENE_LOOK.factionSite,
  };

  /** Which of the six symbols every hold wears now (R633). */
  private symbol: HoldSymbolId = DEFAULT_HOLD_SYMBOL;
  /** Which reading the symbols are drawn in. `off` gives the modern one. */
  private era: HoldEra = 'm';

  /**
   * `self` is the viewer's player id, so their own hold can stand out, and
   * `viewerFaction` is the banner they sail under, so their own faction's
   * installations wear the squadmate variant of card 02. A viewer with no seat
   * has neither: 0 means "nothing here is mine".
   */
  constructor(
    batches: BatchSource, sites: Site[] = [],
    private self = 0, private viewerFaction = 0,
    glyphs?: ChartGlyphs,
  ) {
    this.quads = batches.newQuads(START_CAPACITY * 2);
    this.halos = batches.newCircles(64);
    this.text = batches.newQuads(64);
    this.frames = batches.newSegments(128);
    this.sprite = (key) => batches.sprite(key);
    this.white = batches.sprite('white');
    this.glyphs = glyphs ?? new ChartGlyphs(batches);
    for (const s of sites) this.add(s);
  }

  /**
   * The look of every hold and every installation on the map (R633).
   *
   * `symbol` is the `hold` field of the Marks body and `era` is the reading
   * of the `chart` setting, with `off` giving the modern one. A change to
   * either re-keys every structure, which is one string per site and is why
   * the caller may write it on every frame: nothing happens while the pair
   * is the pair the layer already has.
   */
  setStyle(symbol: HoldSymbolId, era: HoldEra): void {
    if (symbol === this.symbol && era === this.era) return;
    this.symbol = symbol;
    this.era = era;
    for (let i = 0; i < this.n; i++) {
      if (this.kind[i] === SiteKind.SITE_KIND_PLAYER_BASE
        || this.kind[i] === SiteKind.SITE_KIND_BASE) this.restyle(i);
    }
  }

  /**
   * The fields of the look this layer's marks answer to (R814): the
   * deposit mark, the own hold mark, the squadmate hold mark, the structure
   * symbol and the faction charge of a site (R1568).
   *
   * A change re-keys every hold, installation and deposit, which is one
   * string per site and is why the caller may write it on every frame:
   * nothing happens while the fields are the ones the layer already has. The scene writes
   * it one time, and again on the layer a hello builds.
   */
  setLook(l: SceneLook): void {
    const m = this.looks;
    if (l.deposit === m.deposit && l.ownHold === m.ownHold
      && l.squadHold === m.squadHold && l.structure === m.structure
      && l.factionSite === m.factionSite) return;
    m.deposit = l.deposit;
    m.ownHold = l.ownHold;
    m.squadHold = l.squadHold;
    // The structure symbol joins the mark key of a control point (R1103),
    // so this walk covers a control point as well. `setStyle` does not: a
    // hold symbol and its reading say nothing about a strategic object.
    m.structure = l.structure;
    // The faction charge of card 01 (R1568) joins the key of a hold and of
    // an installation, because the atlas bakes it into the cell.
    m.factionSite = l.factionSite;
    // `add` admits these four kinds alone, so every site restyles.
    for (let i = 0; i < this.n; i++) this.restyle(i);
  }

  /** The three look ids in force. */
  get look(): Readonly<SiteLook> { return this.looks; }

  get count(): number { return this.n; }
  get visible(): boolean { return this.shown; }
  setVisible(v: boolean): void { this.shown = v; }

  /**
   * Fades everything the layer draws, as one uniform per batch (R499, R838).
   *
   * Stacked Deck settles the sites onto the map after the ownership
   * (`render/spawn.ts:495`), and the reference does it with the alpha of the
   * container they all sit in. There is no container here, so the fade is
   * the batch fade of `batches.ts` — one uniform written on `flush`, and not
   * a scale on the alpha of every instance the pack writes.
   *
   * The arrival puts it back to 1 on every exit path it has, so a batch can
   * never be left dim by a spawn that ended some other way.
   */
  setAlpha(a: number): void {
    this.quads.globalAlpha = a;
    this.halos.globalAlpha = a;
    this.text.globalAlpha = a;
    this.frames.globalAlpha = a;
  }

  // -------------------------------------------------------------------------
  // The set
  // -------------------------------------------------------------------------

  private grow(): void {
    const cap = this.cap * 2;
    growColumns(this, SITE_COLUMNS, cap);
    this.cap = cap;
  }

  /**
   * What a hold is to the viewer (R633). The rank reads by shape, and the
   * faction by ink, so a hold that is nobody's in particular is `other`.
   *
   * A ruin is not a rank: it has no faction and no owner to compare, and
   * `restyle` answers it before it asks this.
   */
  private rankOf(d: Site): HoldRank {
    if (this.self !== 0 && d.ownerPlayer === this.self) return 'own';
    if (d.ownerPlayer !== 0 && this.squadIds.has(d.ownerPlayer)) return 'mate';
    if (this.viewerFaction !== 0 && d.faction === this.viewerFaction) return 'faction';
    return 'other';
  }

  /** The atlas keys a site wears now, by the scheme of `gfx/atlas.ts`. */
  private restyle(i: number): void {
    const d = this.data.get(this.id[i])!;
    // Only an own hold carries a light (§6.2) and a part that moves (R834).
    // Every other site clears both here, so no branch below can leave a
    // stale one.
    this.beaconKey[i] = '';
    this.animParts[i] = null;
    if (d.kind === SiteKind.SITE_KIND_PLAYER_BASE) {
      if (d.faction === 0) {
        // A hold with no faction is a ruin: dark, lightless, and unlit
        // even for a joiner who gets the fallen holds in the hello (R126).
        this.markKey[i] = siteHoldRuinKey(this.symbol, this.era);
        this.mate[i] = 0;
        this.relabel(i, d, 'ruin');
        return;
      }
      const rank = this.rankOf(d);
      // The hold mark of R834 joins the key, because the atlas bakes it
      // into the cell under the symbol. A plain rank wears no mark.
      const mark = rank === 'own' ? this.looks.ownHold
        : rank === 'mate' ? this.looks.squadHold : '-';
      // A hold of ANOTHER faction wears its charge beside the symbol
      // (R1568): the rank that reads by ink alone gets a shape as well.
      const charge = rank === 'other' && this.looks.factionSite === 'charge';
      this.markKey[i] = siteHoldKey(this.symbol, this.era, d.faction, rank, mark, charge);
      // Only the viewer's own hold blinks (§6.2). Everything that moves on
      // it is one mark of its own, so the movement costs one alpha a frame.
      if (rank === 'own') {
        this.beaconKey[i] = siteHoldLightKey(this.symbol, this.era);
        this.animParts[i] = OWN_HOLD_MARKS[this.looks.ownHold].anim ?? null;
      }
      this.mate[i] = rank === 'mate' ? 1 : 0;
      this.relabel(i, d, rank);
    } else if (d.kind === SiteKind.SITE_KIND_BASE) {
      // A faction installation wears card 02, the light (R633, R641). It
      // carries no plate and no text: the plate says "yours" about a HOLD.
      // It never blinks: the squadmate variant of the card stands still.
      this.relabel(i, d, null);
      if (d.faction === 0) {
        this.markKey[i] = siteInstRuinKey(this.era);
        return;
      }
      const own = this.viewerFaction !== 0 && d.faction === this.viewerFaction;
      // It wears no charge: the chart furniture of its faction says as much
      // (R1568, R1646).
      this.markKey[i] = siteInstKey(this.era, d.faction, own);
    } else if (d.kind === SiteKind.SITE_KIND_DEPOSIT) {
      this.markKey[i] = siteDepositKey(
        this.looks.deposit, d.resource, depositLevel(d.amount, d.capacity));
    } else if (d.kind === SiteKind.SITE_KIND_CONTROL_POINT) {
      // The structure symbol (R1103, R1104): the set, the object kind, the
      // holder and the faction that pledged. The attacker joins the key only
      // while a pledge stands, so a stale faction cannot paint a tell, and
      // never under a film, which shows no siege of today (R2404).
      this.markKey[i] = siteCpKey(this.looks.structure, this.objKind[i],
        d.faction, this.contested[i] === 1 && !this.filming ? this.attacker[i] : 0);
    }
  }

  /**
   * The text one hold writes, and the ink it writes in (R633).
   *
   * `rank` is null for a site that is not a hold, which writes nothing. The
   * historical reading writes the commander's initials and the modern one
   * writes the empire's designation; a hold whose commander the wire has
   * not named writes nothing at all, rather than a bare dash.
   */
  private relabel(i: number, d: Site, rank: HoldRank | 'ruin' | null): void {
    const slot = rank === null ? null : holdLabel(this.symbol, this.era, rank);
    this.labelSlot[i] = slot;
    this.labelText[i] = slot === null
      ? '' : holdText(this.era, rank === 'ruin', d.commander, d.ownerPlayer);
    this.labelInk[i] = slot === null || slot.white ? WHITE
      : (rank === 'ruin' ? RUIN_INK : factionColor(d.faction));
  }

  add(d: Site): void {
    if (this.idToIndex.has(d.id)) return;
    if (d.kind !== SiteKind.SITE_KIND_BASE && d.kind !== SiteKind.SITE_KIND_PLAYER_BASE
      && d.kind !== SiteKind.SITE_KIND_DEPOSIT && d.kind !== SiteKind.SITE_KIND_CONTROL_POINT) {
      return; // the PixiJS layer draws no other kind either
    }
    // Deposits never reach the chart, so only a structure stales it (R131).
    if (d.kind !== SiteKind.SITE_KIND_DEPOSIT) this.structuresCache = null;
    if (this.n === this.cap) this.grow();
    const i = this.n++;
    const [x, y] = cellCenter(d.cell);
    this.id[i] = d.id;
    this.x[i] = x; this.y[i] = y;
    this.restocked[i] = 0;
    this.kind[i] = d.kind;
    this.hidden[i] = 0;
    this.mate[i] = 0;
    this.effect[i] = d.effect;
    // The Sieges frame names the object kind; until it lands the site is
    // the unspecified kind and wears the unkeyed mark of today (R1104).
    this.objKind[i] = 0;
    this.contested[i] = 0;
    this.attacker[i] = 0;
    this.labelSlot[i] = null;
    this.labelText[i] = '';
    this.labelInk[i] = WHITE;
    this.idToIndex.set(d.id, i);
    this.data.set(d.id, d);
    this.restyle(i);
  }

  /**
   * Drops a site the world no longer has (SiteRemoved, R204). The
   * seaquake's stage 2 kills every deposit for good, so the field is
   * deleted rather than dimmed: a marker that stays on the map promises a
   * hauler somewhere to go, and there is nothing there any more.
   */
  remove(siteId: number): void {
    const i = this.idToIndex.get(siteId);
    if (i === undefined) return;
    if (this.kind[i] !== SiteKind.SITE_KIND_DEPOSIT) this.structuresCache = null;
    this.idToIndex.delete(siteId);
    this.data.delete(siteId);
    const last = this.n - 1;
    if (i !== last) {
      moveSlot(this, SITE_COLUMNS, i, last);
      moveSlot(this, SITE_LISTS, i, last);
      this.idToIndex.set(this.id[i], i);
    }
    for (const name of SITE_LISTS) this[name].length = last;
    this.n = last;
  }

  /**
   * The reach of a held Coral Ward's aura, in world px, from
   * `Hello.catalog.rules.ward_aura_px` (R1082). It is 0 until a hello
   * lands, and 0 draws no ring: the client measures no distance of its own
   * and mirrors no rule of the trade.
   */
  wardAuraPx = 0;

  /**
   * Whether the cartouche of a hold draws its text and its frame (R1535).
   * The reel page sets it false, because a mode reel names no commander.
   * The mark itself draws either way.
   */
  labels = true;

  /**
   * Every HELD Coral Ward, in world px (R1082).
   *
   * A Site record carries the site KIND and not the object kind, so the
   * layer cannot tell a Ward from a Throne by itself. The Sieges frame
   * states every object's kind, cell and holder, and the scene hands the
   * held Wards over from there. The array is kept, never copied.
   */
  private wards: readonly WardAura[] = [];

  /** Names the map's held Coral Wards (R1082). */
  setWards(list: readonly WardAura[]): void {
    this.wards = list;
  }

  /**
   * The capture rings of an arcade map (R2029). The array is kept, never
   * copied: the match writes the taker and the count in place.
   */
  private captures: readonly CaptureMark[] = [];

  /** The text of a whole count of seconds, built once a number, so a
   *  running capture builds no string on a frame (§10.4). */
  private readonly countText: string[] = [];

  /** Names the capture rings of the arcade structures (R2029). */
  setCaptures(list: readonly CaptureMark[]): void {
    this.captures = list;
  }

  /**
   * The site ids of the objective structures of an arcade map (R2029). The
   * array is kept, never copied; a season map names none.
   */
  private objectives: readonly number[] = [];

  /** Names the objective structures, which wear the objective tell. */
  setObjectives(siteIds: readonly number[]): void {
    this.objectives = siteIds;
  }

  /**
   * Names what every strategic object IS, from the Sieges frame (R1104).
   *
   * The kind, the contested flag and the attacker are KEPT per site: a
   * `SiteOwner` frame carries none of the three, so `restyle` must never
   * lose them. Only a control point whose kind, contested flag or attacker
   * moved is re-keyed, which is one string per changed object; the frame
   * lands at join and on every pledge edge, so this may be written whole
   * every time.
   */
  setObjects(list: readonly StructureState[]): void {
    for (const o of list) {
      const i = this.idToIndex.get(o.siteId);
      if (i === undefined) continue;
      if (this.kind[i] !== SiteKind.SITE_KIND_CONTROL_POINT) continue;
      const contested = o.contested ? 1 : 0;
      if (this.objKind[i] === o.kind && this.contested[i] === contested
        && this.attacker[i] === o.attacker) continue;
      this.objKind[i] = o.kind;
      this.contested[i] = contested;
      this.attacker[i] = o.attacker;
      this.restyle(i);
    }
  }

  /**
   * Opens or closes a replay film (R2404). The film keeps the sites of
   * today (R542) and drops the contested tell, so only a control point
   * under a pledge is re-keyed, and only on an edge of the flag.
   */
  setFilming(on: boolean): void {
    if (this.filming === on) return;
    this.filming = on;
    for (let i = 0; i < this.n; i++) if (this.contested[i] === 1) this.restyle(i);
  }

  setOwner(
    siteId: number, faction: number, amount: number, capacity = 0, effect = 0,
  ): void {
    const i = this.idToIndex.get(siteId);
    if (i === undefined) return;
    const d = this.data.get(siteId)!;
    if (d.kind === SiteKind.SITE_KIND_DEPOSIT) {
      const wasEmpty = d.amount === 0;
      d.amount = amount;
      d.capacity = capacity;
      // The stock is the fill of the diamond, so a field that crosses a step
      // moves to the sprite of its new step (R475). `restyle` builds the key
      // again, which is one string, and the atlas bakes each step one time.
      this.restyle(i);
      // A field that silted back up blooms one time, so it reads as news.
      if (wasEmpty && amount > 0) this.restocked[i] = 1;
      return;
    }
    d.faction = faction;
    // EVERY owner frame of a player base states the hold effect (R910),
    // because the message is applied whole: a resource update that left the
    // field out would take the mark off the hold.
    d.effect = effect;
    this.effect[i] = effect;
    this.structuresCache = null; // the chart holds a copy of the faction (R131)
    this.restyle(i);
  }

  /**
   * Names the viewer's squadmates and re-styles every hold whose plate that
   * changes. A base carries its plate in its mark, so a roster that lands
   * after the hello must move those holds to another mark. No mark this
   * layer draws prints a squad slot (R185), so it takes the ids alone.
   */
  setSquadIds(ids: Set<number>): void {
    this.squadIds = ids;
    for (let i = 0; i < this.n; i++) {
      if (this.kind[i] !== SiteKind.SITE_KIND_PLAYER_BASE) continue;
      const d = this.data.get(this.id[i])!;
      if (d.faction === 0) continue; // a ruin gets no plate
      if ((this.rankOf(d) === 'mate') === (this.mate[i] === 1)) continue;
      this.restyle(i);
    }
  }

  // -------------------------------------------------------------------------
  // The lookups. None of them reads the cull bit.
  // -------------------------------------------------------------------------

  get(siteId: number): Site | undefined { return this.data.get(siteId); }

  /** Nearest site to a world point, within radius. */
  pick(x: number, y: number, radius: number): Site | null {
    return this.siteAt(this.nearest(x, y, radius, false));
  }

  /**
   * Nearest deposit to a world point, within radius — the right-click snap
   * target for haulers (R80): a click near a field means the field, whatever
   * fleet sprite happens to sit closer.
   */
  pickDeposit(x: number, y: number, radius: number): Site | null {
    return this.siteAt(this.nearest(x, y, radius, true));
  }

  /** The wire record of slot `i`, or null for the -1 of an empty walk. */
  private siteAt(i: number): Site | null {
    return i < 0 ? null : this.data.get(this.id[i]) ?? null;
  }

  /**
   * The deposit inside `radius` of a point, for the gather tracker (R1120,
   * R1121).
   *
   * It is `pickDeposit` in a shape the tracker reads, over ONE reused record
   * (R131), so a per-frame walk of every Dredger allocates nothing. The
   * caller reads the answer inside its own call and keeps no reference.
   */
  fieldNear(x: number, y: number, radius: number): GatherField | null {
    const i = this.nearest(x, y, radius, true);
    const s = this.siteAt(i);
    if (!s) return null;
    const f = this.fieldBuf;
    f.id = s.id;
    f.x = this.x[i];
    f.y = this.y[i];
    f.resource = s.resource;
    f.amount = s.amount;
    f.capacity = s.capacity;
    return f;
  }

  /** The one record `fieldNear` answers with (R131). */
  private readonly fieldBuf: GatherField = {
    id: 0, x: 0, y: 0, resource: 1, amount: 0, capacity: 0,
  };

  /**
   * The deposits whose MARK another layer draws itself (R1120).
   *
   * The `drain-pulse` harvesting look draws the field mark and the work on
   * it as one piece, so this layer's own sprite must not stand under it. The
   * set is the gather layer's own, and it holds the fields a hull cuts now.
   */
  setFieldSuppressed(ids: ReadonlySet<number> | null): void {
    this.fieldSuppressed = ids;
  }

  private fieldSuppressed: ReadonlySet<number> | null = null;

  /**
   * The walk the three picks share: the slot of the nearest site inside
   * `radius`, or -1. `deposits` leaves every other kind out.
   */
  private nearest(x: number, y: number, radius: number, deposits: boolean): number {
    let best = -1;
    let bestD = radius * radius;
    for (let i = 0; i < this.n; i++) {
      if (deposits && this.kind[i] !== SiteKind.SITE_KIND_DEPOSIT) continue;
      const dx = this.x[i] - x, dy = this.y[i] - y;
      const d = dx * dx + dy * dy;
      if (d < bestD) { bestD = d; best = i; }
    }
    return best;
  }

  /**
   * The fixed structures for the minimap (R45): bases, holds, CPs.
   * Identity-stable (R131): the same array comes back every frame until a
   * site is added, removed or changes owner, so a caller can compare
   * identity instead of content. Callers must not mutate it.
   */
  structures(): { x: number; y: number; faction: number; kind: number }[] {
    let out = this.structuresCache;
    if (out) return out;
    out = [];
    for (let i = 0; i < this.n; i++) {
      if (this.kind[i] === SiteKind.SITE_KIND_DEPOSIT) continue; // chart, not treasure map
      const d = this.data.get(this.id[i])!;
      out.push({ x: this.x[i], y: this.y[i], faction: d.faction, kind: d.kind });
    }
    this.structuresCache = out;
    return out;
  }

  /**
   * The sonar ring to light for the site under the cursor (§5).
   *
   * The client does not decide who hears through what (R112) — the server
   * sends the rings this commander receives, and this looks the hovered site
   * up in that list. Hovering a base that grants you nothing lights nothing,
   * which is the honest answer.
   */
  hoverRing(siteId: number, rings: readonly SonarRing[], viewer: number): HoverRing | null {
    const i = this.idToIndex.get(siteId);
    if (i === undefined) return null;
    const d = this.data.get(siteId)!;
    for (const r of rings) {
      // Rings arrive in mpx; a site's px position is what we hold.
      const dx = r.xMpx / MPX_PER_PX - this.x[i], dy = r.yMpx / MPX_PER_PX - this.y[i];
      if (dx * dx + dy * dy < 1) {
        return { x: this.x[i], y: this.y[i], range: r.rangePx, own: d.faction === viewer };
      }
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // The frame
  // -------------------------------------------------------------------------

  update(frame: FrameInfo): void {
    this.advance(frame.dtMs, frame.cull);
  }

  /**
   * Animates the sites on screen, and only those (R186, §10.7).
   *
   * A map carries 498 sites and shows about 99 of them, so the cull is most
   * of the layer's cost. The phase counter walks EVERY site, culled or not:
   * it is what keeps two neighbouring fields out of step, and a phase that
   * depended on who is on screen would jump when the camera moves.
   */
  private advance(deltaMS: number, cull: ViewRect): void {
    this.time += deltaMS / 1000;
    const { x0, y0, x1, y1 } = cull;
    this.quads.begin();
    this.halos.begin();
    this.text.begin();
    this.frames.begin();
    const spin = this.time * 1000 * CP_HALO_SPIN_PER_MS;
    for (let i = 0; i < this.n; i++) {
      // `i + 1` is the phase, so it matches the PixiJS layer's own counter.
      const phase = i + 1;
      // A refilled field swells one time and settles back (R475). The bloom
      // decays before the cull, so a field off screen settles on the same
      // clock as one on screen (R1360).
      if (this.restocked[i] > 0) {
        this.restocked[i] = Math.max(0, this.restocked[i] - deltaMS / RESTOCK_MS);
      }
      const hidden = outside(this.x[i], this.y[i], x0, y0, x1, y1);
      this.hidden[i] = hidden ? 1 : 0;
      if (hidden) continue;
      const x = this.x[i], y = this.y[i];
      const d = this.data.get(this.id[i])!;
      if (this.kind[i] === SiteKind.SITE_KIND_CONTROL_POINT
        && this.looks.structure === DEFAULT_STRUCTURE_SET) {
        // The halo turns under the core. It is an SDF circle and not a
        // sprite, because a dashed ring at one radius is one instance.
        //
        // **The atlas bakes the still mark** (R1104). This is the one live
        // part, and it belongs to the `default` set alone: card 04 is the
        // mark of today and keeps the halo that ships today. The other nine
        // sets draw NO live halo, so a set's motion is never its tell.
        //
        // The dash count is the kind's (card 04). A site whose kind the
        // Sieges frame has not named yet takes the eight dashes of the
        // unkeyed mark of today.
        const k = structureKindOf(this.objKind[i]);
        if (k === null) {
          this.halos.dashed(x, y, CP_HALO_R, CP_HALO_WIDTH,
            CP_HALO_DASHES, CP_HALO_FILL, factionColor(d.faction), CP_HALO_ALPHA,
            spin % TAU);
        } else {
          const h = DEFAULT_HALOS[k];
          this.halos.dashed(x, y, h.r, h.width, h.dashes, h.fill,
            factionColor(d.faction),
            d.faction === 0 ? CP_HALO_NEUTRAL_ALPHA : CP_HALO_HELD_ALPHA,
            spin % TAU);
        }
      }
      const scale = 1 + this.restocked[i] * RESTOCK_SCALE;
      // A site mark is baked at its path origin, so the entry's offset
      // carries the placement and the quad is origin-relative. A field whose
      // mark a harvesting look draws itself is left out (R1120).
      const drawnElsewhere = this.fieldSuppressed !== null
        && this.fieldSuppressed.has(this.id[i]);
      if (!drawnElsewhere) {
        this.quads.sprite(x, y, 0, this.sprite(this.markKey[i]), NO_TINT, 1, scale, 0, false);
      }
      // The cartouche's own text (R633). It is world px like the mark, so
      // it grows with the map, and it is laid out again here every frame:
      // the atlas re-bakes a glyph cell when the vendored faces land, and
      // this reads the entries of THIS frame. The frame round it follows
      // the run's width, because a three-figure designation is wider than
      // any box a cell could bake, and it goes down before the light so the
      // light at the box corner stays on top of the fill.
      const slot = this.labels ? this.labelSlot[i] : null;
      let lightDx = 0;
      if (slot) {
        const label = this.labelText[i];
        const ink = this.labelInk[i];
        const w = label === '' ? 0 : this.glyphs.run(this.text, label, x + slot.dx, y + slot.dy,
          slot.face, slot.sizePx, ink, 1, LABEL_RUN);
        const F = HOLD_LABEL_FRAME;
        const half = Math.max(w, F.minInner) / 2 + F.padX;
        if (slot.frame === 'box') {
          const e = this.white;
          this.quads.quad(x, y, 0, F.top + F.height / 2, half * 2, F.height, 0, 0,
            F.fill, F.fillAlpha, e.u0, e.v0, e.u1, e.v1);
          const top = y + F.top, bottom = top + F.height;
          this.frames.line(x - half, top, x + half, top, F.width, ink, F.alpha);
          this.frames.line(x + half, top, x + half, bottom, F.width, ink, F.alpha);
          this.frames.line(x + half, bottom, x - half, bottom, F.width, ink, F.alpha);
          this.frames.line(x - half, bottom, x - half, top, F.width, ink, F.alpha);
          if (slot.lightAtFrameCorner) lightDx = -half;
        } else if (slot.frame === 'rule') {
          this.frames.line(x - half + F.ruleInset, y + F.ruleDy, x + half - F.ruleInset, y + F.ruleDy,
            F.ruleWidth, ink, F.alpha);
        }
        if (slot.strike && label !== '') {
          const b = HOLD_LABEL_STRIKE;
          this.frames.line(x - w / 2 - b.pad, y + slot.dy + b.y, x + w / 2 + b.pad, y + slot.dy + b.y,
            b.width, ink, b.alpha);
        }
      }
      const light = this.beaconKey[i];
      if (light) {
        const blink = BLINK_BASE + BLINK_SWING * Math.abs(Math.sin(this.time * BLINK_HZ + phase));
        this.quads.sprite(x + lightDx, y, 0, this.sprite(light), NO_TINT, blink, 1, 0, false);
      }
      // The parts of the hold mark that move (R834), beside the light and
      // under the same rule: only the viewer's own hold carries one. The
      // card's own alpha and scale ride the quad, so the cell is baked one
      // time and the movement costs one number a part a frame.
      const parts = this.animParts[i];
      if (parts) {
        for (let k = 0; k < parts.length; k++) {
          const part = parts[k];
          const tint = part.tint === 'white' ? WHITE : factionColor(d.faction);
          const swell = part.scale === undefined ? 1 : part.scale(this.time);
          this.quads.sprite(x, y, 0, this.sprite(part.key), tint,
            part.alpha(this.time), scale * swell, 0, false);
        }
      }
    }
    this.packWardRings(cull);
    this.packObjectives();
    this.packCaptures(cull);
  }

  /**
   * The objective tell of every objective structure (R2029): the pennant
   * of the map designer, baked white one time as the site cell `site:flag`
   * and tinted with the owner ink, or the neutral grey while nobody owns
   * the structure. It rides the site's own cull and fade, so it stands
   * and goes with the mark. A season map names no objective and pays one
   * compare.
   */
  private packObjectives(): void {
    const n = this.objectives.length;
    if (n === 0) return;
    for (let k = 0; k < n; k++) {
      const id = this.objectives[k];
      const i = this.idToIndex.get(id);
      if (i === undefined || this.hidden[i] === 1) continue;
      // factionColor(0) is the neutral grey.
      const faction = this.data.get(id)?.faction ?? 0;
      this.quads.sprite(this.x[i], this.y[i], 0, this.sprite(OBJECTIVE_FLAG_KEY),
        factionColor(faction), 1, 1, 0, false);
    }
  }

  /**
   * The ring and the count of every running capture (R2029): the outline
   * of the cell and the six cells round it in the taker's ink, and the
   * whole wall seconds left over the mark. Only a capture that runs draws,
   * so a map at rest pays one compare a structure.
   */
  private packCaptures(cull: ViewRect): void {
    const n = this.captures.length;
    if (n === 0) return;
    const pad = 3 * HEX_SIZE;
    for (let i = 0; i < n; i++) {
      const c = this.captures[i];
      if (c.taker === 0) continue;
      if (outside(c.x, c.y, cull.x0 - pad, cull.y0 - pad, cull.x1 + pad, cull.y1 + pad)) continue;
      const ink = factionColor(c.taker);
      const e = c.edges;
      for (let k = 0; k + 3 < e.length; k += 4) {
        this.frames.line(e[k], e[k + 1], e[k + 2], e[k + 3], CAPTURE_RING_WIDTH, ink, CAPTURE_RING_ALPHA);
      }
      const left = Math.max(0, Math.ceil(c.leftWallSec));
      const count = this.countText[left] ??= String(left);
      this.glyphs.run(this.text, count, c.x, c.y + CAPTURE_COUNT_DY * HEX_SIZE,
        'mono', CAPTURE_COUNT_PX, ink, 1, LABEL_RUN);
    }
  }

  /**
   * The aura ring of every HELD Coral Ward (R1082, §6.2).
   *
   * The ring is the reach the Ward mends inside, drawn in the holding
   * faction's ink. A NEUTRAL Ward draws none, because a neutral Ward mends
   * nothing: the mark says what the object is doing and not what it could
   * do for whoever takes it.
   *
   * It is a dashed circle at the line weight of the hovered base's sonar
   * ring, so a Ward reads as a reach a commander stands in and does not
   * out-shout the Trench Throne.
   *
   * The cull is the window widened by the reach, and not the site's own
   * `hidden` bit: a Ward off the glass still throws its ring into the
   * picture, exactly as a set Tender off the glass throws its aura.
   */
  private packWardRings(cull: ViewRect): void {
    const r = this.wardAuraPx;
    if (r <= 0 || this.wards.length === 0) return;
    for (const w of this.wards) {
      // A public Ward of an arcade map mends every hull, so it draws its
      // ring in the neutral grey (R2029).
      if (w.faction === 0 && !w.public) continue;
      if (outside(w.x, w.y, cull.x0 - r, cull.y0 - r, cull.x1 + r, cull.y1 + r)) continue;
      this.halos.dashed(w.x, w.y, r, WARD_RING_WIDTH, WARD_RING_DASHES,
        WARD_RING_DASH_FILL, factionColor(w.faction), WARD_RING_ALPHA);
    }
  }

  private holdBuf: HoldSnap[] = [];

  /**
   * Snapshot of every player base, for the effects layer (R910, §6.6).
   *
   * Only a player base ever wears an effect, so the walk answers those and
   * nothing else. The buffer and its objects are reused (R131): a reader
   * must use them inside the frame and must not keep the reference.
   * `hidden` is the answer the last `advance` came to, so the effects layer
   * culls nothing of its own.
   */
  holds(): readonly HoldSnap[] {
    const out = this.holdBuf;
    let n = 0;
    for (let i = 0; i < this.n; i++) {
      if (this.kind[i] !== SiteKind.SITE_KIND_PLAYER_BASE) continue;
      let o = out[n];
      if (!o) {
        o = {
          id: 0, x: 0, y: 0, faction: 0, ownerPlayer: 0,
          effect: 0, hidden: false,
        };
        out[n] = o;
      }
      const d = this.data.get(this.id[i])!;
      o.id = this.id[i];
      o.x = this.x[i]; o.y = this.y[i];
      o.faction = d.faction;
      o.ownerPlayer = d.ownerPlayer;
      o.effect = this.effect[i];
      o.hidden = this.hidden[i] === 1;
      n++;
    }
    out.length = n;
    return out;
  }

  draw(ctx: DrawCtx): void {
    this.halos.flush(ctx.cam);
    this.quads.flush(ctx.cam);
    // The labels after the marks, and the frames last: a box edge or a
    // strike bar under the letters says nothing (R633).
    this.text.flush(ctx.cam);
    this.frames.flush(ctx.cam);
  }
}
